import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { postConfirmStripePayment, type ConfirmStripePaymentResult } from '@/api/modules/subpayment';
import { ApiRequestError } from '@/api/client';
import {
  isSubscribePaymentResultState,
  type SubscribePaymentResultLocationState,
} from '@/lib/stripePaymentResult';

type PagePhase = 'confirming' | 'done' | 'failed';

/** Seconds between each `confirm_stripe_payment` request. */
const QUERY_INTERVAL_SEC = 3;
/** Stop polling after this many attempts (~1 minute plus request time). */
const MAX_QUERY_ATTEMPTS = 20;

function billingLabel(billing: SubscribePaymentResultLocationState['billing']) {
  return billing === 'monthly' ? 'Monthly' : 'Yearly';
}

function errorMessage(e: unknown): string {
  return e instanceof ApiRequestError
    ? e.message
    : 'Failed to confirm payment. Please contact support.';
}

function isOrderPaid(result: ConfirmStripePaymentResult): boolean {
  return result.status === 1;
}

export default function SubscribePaymentResultPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = isSubscribePaymentResultState(location.state) ? location.state : null;

  const [phase, setPhase] = useState<PagePhase>('confirming');
  const [serverResult, setServerResult] = useState<ConfirmStripePaymentResult | null>(null);
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(QUERY_INTERVAL_SEC);
  const [querying, setQuerying] = useState(false);

  useEffect(() => {
    if (!state) {
      navigate('/subscribe', { replace: true });
      return;
    }

    if (state.stripe.status !== 'succeeded') {
      setServerMessage(`Stripe payment status: ${state.stripe.status}`);
      setPhase('failed');
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let countdownTimer: number | undefined;
    let countdown = QUERY_INTERVAL_SEC;

    const clearCountdown = () => {
      if (countdownTimer !== undefined) {
        window.clearInterval(countdownTimer);
        countdownTimer = undefined;
      }
    };

    const queryPayment = async () => {
      if (cancelled) return;
      setQuerying(true);
      setSecondsLeft(0);
      attempts += 1;

      try {
        const confirmed = await postConfirmStripePayment(
          state.stripe.orderId,
          state.stripe.paymentIntentId,
        );
        if (cancelled) return;
        if (isOrderPaid(confirmed)) {
          setServerResult(confirmed);
          setServerMessage(null);
          setPhase('done');
          return;
        }
        setServerMessage('Payment is still being processed.');
      } catch (e) {
        if (cancelled) return;
        setServerMessage(errorMessage(e));
      }

      if (cancelled) return;

      if (attempts >= MAX_QUERY_ATTEMPTS) {
        setPhase('failed');
        setQuerying(false);
        return;
      }

      setQuerying(false);
      startCountdown();
    };

    const startCountdown = () => {
      clearCountdown();
      countdown = QUERY_INTERVAL_SEC;
      setSecondsLeft(countdown);
      countdownTimer = window.setInterval(() => {
        if (cancelled) return;
        countdown -= 1;
        if (countdown <= 0) {
          clearCountdown();
          void queryPayment();
          return;
        }
        setSecondsLeft(countdown);
      }, 1000);
    };

    setQuerying(false);
    startCountdown();

    return () => {
      cancelled = true;
      clearCountdown();
    };
  }, [state, navigate]);

  if (!state) {
    return null;
  }

  const { stripe, billing, amountLabel } = state;
  const stripeSucceeded = stripe.status === 'succeeded';
  const overallSuccess = phase === 'done' && stripeSucceeded && serverResult !== null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-100 px-4 py-2.5">
        <h1 className="text-gray-900 font-bold text-lg text-center">Payment result</h1>
      </header>

      <main className="flex-1 px-4 py-8 max-w-md mx-auto w-full">
        {phase === 'confirming' && (
          <div className="flex flex-col items-center text-center py-12">
            {querying ? (
              <i className="ri-loader-4-line animate-spin text-green-500 text-4xl mb-4" />
            ) : (
              <div className="w-16 h-16 mb-4 rounded-full border-2 border-green-200 flex items-center justify-center">
                <span className="text-green-600 font-bold text-2xl tabular-nums">{secondsLeft}</span>
              </div>
            )}
            <p className="text-gray-800 text-sm font-medium">
              Waiting for Stripe to return the payment result
            </p>
            <p className="text-gray-500 text-xs mt-2 leading-relaxed max-w-xs">
              {querying
                ? 'Checking with Stripe now. This usually takes a few seconds.'
                : `We'll check again in ${secondsLeft}s. Please keep this page open.`}
            </p>
          </div>
        )}

        {phase !== 'confirming' && (
          <div className="space-y-3">
            <div
              className={`rounded-lg p-3 text-center ${overallSuccess ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'
                }`}
            >
              <div
                className={`w-16 h-16 mx-auto flex items-center justify-center rounded-full mb-4 ${overallSuccess ? 'bg-green-100' : 'bg-red-100'
                  }`}
              >
                <i
                  className={`text-4xl ${overallSuccess
                      ? 'ri-checkbox-circle-fill text-green-500'
                      : 'ri-close-circle-fill text-red-500'
                    }`}
                />
              </div>
              <h2
                className={`font-bold text-xl mb-1 ${overallSuccess ? 'text-green-800' : 'text-red-800'
                  }`}
              >
                {overallSuccess ? 'Payment successful' : 'Payment failed'}
              </h2>
              {!overallSuccess && serverMessage && (
                <p className="text-red-600 text-sm mt-2">{serverMessage}</p>
              )}
            </div>

            <section className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Stripe
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Payment Intent</dt>
                  <dd className="text-gray-800 font-mono text-xs text-right break-all">
                    {stripe.paymentIntentId}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Status</dt>
                  <dd
                    className={`font-medium capitalize ${stripeSucceeded ? 'text-green-600' : 'text-red-600'
                      }`}
                  >
                    {stripe.status}
                  </dd>
                </div>
              </dl>
            </section>

            {serverResult && (
              <section className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Order
                </h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">Order ID</dt>
                    <dd className="text-gray-800 font-medium">{String(serverResult.orderId)}</dd>
                  </div>
                  {serverResult.paymentTime && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">Paid at</dt>
                      <dd className="text-gray-800 text-right">{serverResult.paymentTime}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">Billing</dt>
                    <dd className="text-gray-800 font-medium">{billingLabel(billing)}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">Amount</dt>
                    <dd className="text-gray-800 font-medium">{amountLabel}</dd>
                  </div>
                </dl>
              </section>
            )}

            <div className="pt-2 space-y-2">
              {overallSuccess ? (
                <button
                  type="button"
                  onClick={() => navigate('/me', { replace: true })}
                  className="w-full py-2.5 rounded-lg bg-green-500 text-white font-semibold text-sm hover:bg-green-600"
                >
                  Back to My Account
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/subscribe', { replace: true })}
                  className="w-full py-2.5 rounded-lg bg-gray-800 text-white font-semibold text-sm hover:bg-gray-900"
                >
                  Try again
                </button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
